import { useState, useEffect, type ReactNode, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiGetPromoKit, apiUnlockPromoKit, type PromoKitPublicDTO, type PromoContentDTO, type PromoContentSection } from '../api';
import { resolveBlockOrder } from '../lib/promoBlockOrder';
import PasswordInput from '../components/PasswordInput';
import PageLoadError from '../components/PageLoadError';

/**
 * Minimal inline markup for the authored bio/intro (option C): **bold**,
 * *italic* and [text](https://url) links only — the editor is a textarea with
 * a small formatting bar (option B, 2026-10-19), so the renderer stays as
 * small as the contract. Links are STRICT: only http(s) hrefs ever become
 * <a>; everything else is plain text (never any HTML passthrough).
 */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]\n]+\]\(https?:\/\/[^)\s]+\))/g;
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) out.push(<strong key={`${keyPrefix}-b${i}`}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('[')) {
      const link = tok.match(/^\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)$/);
      if (link) out.push(<a key={`${keyPrefix}-l${i}`} href={link[2]} target="_blank" rel="noopener noreferrer">{link[1]}</a>);
      else out.push(tok);
    } else out.push(<em key={`${keyPrefix}-i${i}`}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length; i++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Turn a free-text contact line into a link when it clearly is one. */
function contactNode(contact: string): ReactNode {
  const s = contact.trim();
  if (/^https?:\/\//i.test(s)) return <a href={s} target="_blank" rel="noreferrer">{s}</a>;
  const email = s.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
  if (email) return <a href={`mailto:${email[0]}`}>{s}</a>;
  return s;
}

/**
 * SOCIAL ICONS (Sandro v2, 2026-10-19) — five hand-drawn minimalist inline
 * SVGs (NO icon library, NO third-party script, NO emoji on the public page —
 * emoji are editor-only). ~26px, currentColor so the icons follow the theme
 * text color, accent on hover (CSS `.sand-social:hover`). Every anchor is
 * target=_blank rel=noopener noreferrer. The URLs come from the blob's
 * `socials` key (http(s)-only, whitelisted server-side).
 */
const SOCIAL_PATHS: Record<string, ReactNode> = {
  // Instagram: rounded square + lens circle + flash dot.
  instagram: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="16.8" cy="7.2" r="1.2" fill="currentColor" />
    </>
  ),
  // TikTok: the note-with-curl silhouette.
  tiktok: (
    <path
      d="M14.5 3h-2.6v12.1a2.6 2.6 0 1 1-2.6-2.6c.2 0 .5 0 .7.1v-2.7a5.3 5.3 0 1 0 4.5 5.2V9.2a6.4 6.4 0 0 0 3.7 1.2V7.7a3.9 3.9 0 0 1-3.7-3.9V3z"
      fill="currentColor"
    />
  ),
  // Spotify: circle + three sound arcs.
  spotify: (
    <>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7.4 9.6c3.1-.8 6.5-.4 9 1.1M7.8 12.6c2.5-.6 5.2-.3 7.3.9M8.2 15.4c2-.5 4-.2 5.6.7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),
  // YouTube: rounded rect + play triangle.
  youtube: (
    <>
      <rect x="3" y="6" width="18" height="12.5" rx="3.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M10.2 9.5l4.8 2.75-4.8 2.75z" fill="currentColor" />
    </>
  ),
  // Website: globe (circle + meridians).
  website: (
    <>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3 12h18M12 3c2.6 2.5 4 5.7 4 9s-1.4 6.5-4 9c-2.6-2.5-4-5.7-4-9s1.4-6.5 4-9z" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </>
  ),
};

export function SocialIcons({ socials, compact = false }: { socials?: PromoContentDTO['socials']; compact?: boolean }) {
  const entries = Object.entries(socials || {}).filter(([, v]) => typeof v === 'string' && /^https?:\/\//i.test(v as string));
  if (!entries.length) return null;
  return (
    <div className={`sand-socials${compact ? ' sand-socials-compact' : ''}`} data-promo-socials>
      {entries.map(([key, url]) => (
        <a
          key={key}
          className="sand-social"
          href={url as string}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={key}
          title={key}
          data-promo-social={key}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" focusable="false" aria-hidden="true">
            {SOCIAL_PATHS[key]}
          </svg>
        </a>
      ))}
    </div>
  );
}

/**
 * The STRUCTURED render (option C) used when the band/tour authored content —
 * now in the "Mailerlite Sandro" design (variant B, 2026-10-19, validated
 * mockup `bandz temp/promo-preview-maquette.html`): dark centered hero →
 * full-width pill buttons for link items → per-section thumbnails strips for
 * file items (real <img> thumbs through the SAME guarded URL the download
 * uses, so the unlock cookie applies; the mockup's gradient tile stays as the
 * loading/fallback face) → YouTube embeds stacked and centered → centered
 * bio → centered quotes → contact. STRUCTURE only: same guarded URLs, same
 * blob as single source, the classic render (no `content`) is untouched.
 */
/**
 * EXPORTED (option B, 2026-10-19): the LIVE PREVIEW of the editor
 * (PromoContentSettings) renders this exact component with the blob being
 * edited — the preview IS the public render, not an approximation.
 */
export function PromoContentPage({ data, content }: { data: PromoKitPublicDTO; content: PromoContentDTO }) {
  const { t } = useTranslation();
  const fmtSize = (n: number) => {
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };
  const fileById = new Map(data.files.map((f) => [f.id, f]));
  // HIDDEN sections (2026-10-20): `hidden: true` skips the section on the
  // public page, but its files stay "placed" so they are NOT dumped into the
  // fallback downloads list — and the ZIP pack (built from ALL promo_kit
  // files server-side) is untouched. Absent key = visible (legacy backcompat).
  const allSections = [...(content.sections || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const sections = allSections.filter((s) => !s.hidden);
  const itemsOf = (sec: PromoContentSection) => sec.items || (sec.fileIds || []).map((id) => ({ type: 'file' as const, fileId: id }));
  const placed = new Set(allSections.flatMap((s) => itemsOf(s).filter((it) => it.type === 'file').map((it) => (it as { fileId: number }).fileId)));
  const rest = data.files.filter((f) => !placed.has(f.id));

  const isImage = (f: { mime: string }) => String(f.mime || '').startsWith('image/');
  const isVideo = (f: { mime: string }) => String(f.mime || '').startsWith('video/');

  /**
   * One file item: the tile IS the download link (2026-10-19 user feedback —
   * no duplicated caption/button row under the image). Real thumb for images,
   * ▶ tile for clips, a generic tile otherwise; a small size badge sits on the
   * tile; the filename lives in title/aria-label for accessibility.
   */
  const fileTile = (f: PromoKitPublicDTO['files'][number]) => (
    <a
      key={f.id}
      className="sand-mini"
      href={f.url}
      target="_blank"
      rel="noreferrer"
      download={f.filename}
      title={f.filename}
      aria-label={f.filename}
    >
      {isImage(f) ? (
        <img src={f.url} alt={f.filename} loading="lazy" />
      ) : isVideo(f) ? (
        // VIDEO TILE FIX (2026-10-20): the thumbnail is a <video> element of
        // the file itself — preload="metadata" shows the FIRST FRAME as the
        // vignette (muted, playsInline, NO autoplay). If the browser cannot
        // decode a frame (codecs), the element just paints its dark gradient
        // background — the exact fallback tile from before. Click = the
        // anchor download, unchanged (guarded route); pointer-events:none
        // keeps the click on the <a>. No server-side poster (no ffmpeg).
        <span className="sand-mini-video" data-promo-video-tile="frame">
          <video
            src={f.url}
            preload="metadata"
            muted
            playsInline
            tabIndex={-1}
            aria-hidden="true"
          />
          <span className="sand-mini-video-play" aria-hidden="true">▶</span>
        </span>
      ) : (
        <span className="sand-mini-file">⬇</span>
      )}
      {fmtSize(f.size) && <span className="sand-mini-size">{fmtSize(f.size)}</span>}
    </a>
  );

  /** video — YouTube ONLY. The server normalizes to a youtube-nocookie embed
   *  URL; this client-side guard is defense in depth: ANY other origin is
   *  never embedded. */
  const embedOf = (it: { url?: string }) => {
    const m = String(it.url || '').match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/i);
    return m ? `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0` : null;
  };

  /**
   * HERO image (2026-10-19): derived automatically, zero new editor field —
   * the first IMAGE of the "artwork" section (key or title, case-insensitive),
   * falling back to the band image, then to the plain dark gradient. The image
   * is served by the SAME guarded route the downloads use (unlock cookie).
   * OPTION B (2026-10-19): an authored `heroImageFileId` in the blob PRIMES
   * over the auto derivation — 0 = explicit "no hero" (dark gradient only);
   * > 0 = that promo_kit image (validated band-scoped server-side; a stale or
   * non-image id simply falls through to AUTO). Absent = exact legacy behavior.
   */
  const artworkSection = sections.find((s) => /artwork/i.test(s.key || '') || /artwork/i.test(s.title || ''));
  const autoHeroUrl =
    (artworkSection
      ? itemsOf(artworkSection)
          .map((it) => (it.type === 'file' ? fileById.get((it as { fileId: number }).fileId) : null))
          .find((f): f is PromoKitPublicDTO['files'][number] => !!f && isImage(f))?.url
      : undefined) || data.band.image_url || '';
  const heroOverride = content.heroImageFileId;
  const heroUrl =
    heroOverride === 0
      ? ''
      : heroOverride
        ? (() => {
            const f = fileById.get(heroOverride);
            return f && isImage(f) ? f.url : autoHeroUrl;
          })()
        : autoHeroUrl;

  /**
   * BLOCK ORDER (spec docs/SPEC-PROMO-BLOCK-ORDER.md, 2026-10-20): the page
   * iterates the blob's `blockOrder` (resolved: unknown/duplicate ids dropped,
   * canonical defaults appended) and renders each content block at its place —
   * intro, bio, quotes, every section, socials, contact. The OLD rule
   * "video-majority sections last" is GONE: the user decides the order. The
   * hero (photo or dark header) always stays first and is never in the list;
   * the full-pack button stays last.
   */
  const blocks = resolveBlockOrder(content.blockOrder, allSections);
  const sectionByKey = new Map(allSections.map((s) => [s.key, s]));
  const visibleSectionBlocks = blocks.filter((id) => {
    if (!id.startsWith('section:')) return false;
    const sec = sectionByKey.get(id.slice('section:'.length));
    return !!sec && !sec.hidden && itemsOf(sec).length > 0;
  });
  const restAfterId = visibleSectionBlocks[visibleSectionBlocks.length - 1] || null;

  const renderSection = (sec: PromoContentSection) => {
    const items = itemsOf(sec);
    if (!items.length) return null;
    const tiles = items
      .filter((it) => it.type === 'file')
      .map((it) => fileById.get((it as { fileId: number }).fileId))
      .filter((f): f is PromoKitPublicDTO['files'][number] => !!f);
    const embeds = items
      .map((it, j) => ({ it, j }))
      .filter(({ it }) => it.type === 'video')
      .map(({ it, j }) => ({ embed: embedOf(it as { url?: string }), j }))
      .filter((v) => !!v.embed);
    return (
      <section key={sec.key || sec.title}>
        {sec.title && <h3 className="sand-sec">{sec.title}</h3>}
        {/* link items → one full-width pill per link (Sandro style) */}
        {items.map((it, j) => {
          if (it.type !== 'link') return null;
          return (
            <a
              key={`link-${j}`}
              className="sand-pill"
              href={it.url}
              target="_blank"
              rel="noopener noreferrer"
              data-promo-link={it.label || it.url}
            >
              ↗ {it.label || it.url}
            </a>
          );
        })}
        {tiles.length > 0 && (
          <div className="sand-strip" data-promo-strip={sec.title || undefined}>
            {tiles.map(fileTile)}
          </div>
        )}
        {embeds.length > 0 && (
          <div className="sand-embeds">
            {embeds.map(({ embed, j }) => (
              <div key={`video-${j}`} style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', marginBottom: 12 }} data-promo-video>
                <iframe
                  src={embed as string}
                  title={`video-${j}`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, borderRadius: 8 }}
                />
              </div>
            ))}
          </div>
        )}
      </section>
    );
  };

  // The band name / tour title / intro / kit label sit BELOW the hero photo
  // (Sandro v2 #1) when a hero image exists; the dark hero keeps the text only
  // in the no-image fallback.
  const headBlock = (inHero: boolean) => (
    <div className={inHero ? undefined : 'sand-head'} data-promo-head>
      {data.band.image_url && inHero && data.band.image_url !== heroUrl && (
        <img className="sand-hero-logo" src={data.band.image_url} alt={data.band.name} />
      )}
      <div className={`sand-hero-artist${inHero ? '' : ' sand-artist-light'}`} data-promo-band-name>{data.band.name}</div>
      {(content.tourTitle || content.tourPeriod) && (
        <p className={`sand-hero-tour${inHero ? '' : ' sand-tour-light'}`} data-promo-tour-title>
          {content.tourTitle}
          {content.tourPeriod && ` — ${content.tourPeriod}`}
        </p>
      )}
      <p className={inHero ? 'sand-hero-kit' : 'sand-kit-light'}>{t('promoKit.title')}</p>
      {!heroUrl && <SocialIcons socials={content.socials} compact />}
    </div>
  );

  return (
    <div data-promo-content-page>
      {heroUrl ? (
        // PHOTO hero: the big image ALONE (full width, ~16/7, cover, rounded
        // corners); the name/tour/intro move to the light block below.
        <div
          className="sand-hero-img"
          style={{ backgroundImage: `url(${heroUrl})` }}
          data-promo-hero
          role="img"
          aria-label={data.band.name}
        />
      ) : (
        <header className="sand-hero" data-promo-hero>{headBlock(true)}</header>
      )}

      {heroUrl && headBlock(false)}

      {blocks.map((id) => {
        if (id === 'intro' && content.intro) {
          return (
            <p key={id} className="sand-bio" style={{ marginTop: 0 }} data-promo-intro>
              {renderInline(content.intro, 'intro')}
            </p>
          );
        }
        if (id === 'bio' && content.bio) {
          return (
            <div key={id} className="sand-bio" data-promo-bio>
              {content.bio.split(/\n{2,}/).map((para, i) => (
                <p key={i} style={{ margin: '0 0 12px', whiteSpace: 'pre-line' }}>{renderInline(para, `bio${i}`)}</p>
              ))}
            </div>
          );
        }
        if (id === 'quotes' && content.quotes?.length > 0) {
          return (
            <div key={id} data-promo-quotes-public>
              {content.quotes.filter((q) => q.text).map((q, i) => (
                <blockquote key={i} className="sand-quote">
                  “{q.text}”
                  {q.source && <span className="sand-quote-src"> — {q.source}</span>}
                </blockquote>
              ))}
            </div>
          );
        }
        if (id.startsWith('section:')) {
          const sec = sectionByKey.get(id.slice('section:'.length));
          if (!sec || sec.hidden) return null; // hidden = skipped, files stay "placed"
          return <div key={id} data-promo-block-public={id}>{renderSection(sec)}</div>;
        }
        if (id === 'socials') {
          return <SocialIcons key={id} socials={content.socials} />;
        }
        if (id === 'contact' && content.contact) {
          return (
            <p key={id} style={{ marginTop: 28, marginBottom: 0, textAlign: 'center' }} data-promo-contact>
              {contactNode(content.contact)}
            </p>
          );
        }
        // The leftover-files "Downloads" group renders right after the LAST
        // visible section block (its old home: inside the sections area).
        if (id === restAfterId && rest.length > 0) {
          return (
            <section key={`${id}-rest`} data-promo-downloads-rest>
              <h3 className="sand-sec">{t('promoContent.downloadsFallback')}</h3>
              <div className="sand-strip">{rest.map(fileTile)}</div>
            </section>
          );
        }
        return null;
      })}

      {data.files.length > 0 && data.token && (
        <a
          className="sand-pack"
          href={`/api/share/promo/${encodeURIComponent(data.token)}/pack`}
          data-promo-pack
        >
          ⬇ {t('promoContent.packDownload')}
        </a>
      )}
    </div>
  );
}

/**
 * PUBLIC page reached from a band's Promo kit share link (/share/promo/:token) —
 * no login, no session. The token is the capability (one per band), so any
 * promoter with the link can browse the band's press kit / logos / promo images
 * and download them. The server returns ONLY the band's own 'promo_kit' files
 * (names + URLs), never show-linked files, never other data. (2026-08-22)
 *
 * PASSWORD (2026-10-16): the band may set an optional password on the link. The
 * GET then answers `401 { passwordRequired: true }` with NO content at all, and
 * this page shows a lock screen (mirroring ShareRiderPage). Unlocking posts the
 * password, which sets a 7-day cookie; the next GET returns the files without
 * asking again. The file BYTES are served by a guarded route too, so the
 * password protects the downloads — not merely the listing.
 */
export default function PromoKitSharePage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [data, setData] = useState<PromoKitPublicDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lot 3a — separate "the request FAILED" from "this link is not valid":
  // both used to collapse into one culprit message that named the link, so a
  // plain network outage told the promoter their share link was dead. The
  // failure keeps its own state (readable + retryable); a rejected token keeps
  // the token message.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  // Password lock state (the link is protected and not unlocked yet).
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [lockBandName, setLockBandName] = useState('');
  const [pw, setPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError(t('promoKit.missingToken'));
      setLoadError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setError(null);
    setLoadError(null);
    setPasswordRequired(false);
    setPwError(null);
    setLoading(true);
    apiGetPromoKit(token)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as { status?: number })?.status;
        const body = (e as { body?: unknown })?.body as
          { passwordRequired?: boolean; band?: { name?: string } } | undefined;
        // 401 + passwordRequired = the LOCK screen, not an invalid link and not
        // an expired session (public routes are exempt from the /login bounce,
        // see the `/api/share/` carve-out in src/api.ts).
        if (status === 401 && body?.passwordRequired) {
          setPasswordRequired(true);
          setLockBandName(body.band?.name || '');
        } else if (status === 401 || status === 403 || status === 404) {
          // A token rejected BY THE SERVER (401/403/404) is the link's problem;
          // anything else is a load failure the user can retry.
          setError(t('promoKit.invalidOrDisabled'));
        } else {
          setLoadError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // `t` is intentionally NOT a dependency (mirrors ShareRiderPage): the
    // messages are only read on the error paths, never stored as state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, retryToken]);

  const unlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pw) return;
    setPwBusy(true);
    setPwError(null);
    try {
      await apiUnlockPromoKit(token, pw);
      // Cookie is set; re-fetch to load the files.
      const d = await apiGetPromoKit(token);
      setData(d);
      setPasswordRequired(false);
    } catch (err) {
      const status = (err as { status?: number })?.status;
      setPwError(status === 401 ? t('sharePromo.wrongPassword') : (err instanceof Error ? err.message : t('sharePromo.wrongPassword')));
    } finally {
      setPwBusy(false);
    }
  };

  const fmtSize = (n: number) => {
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Password-protected link not unlocked yet — show the lock screen and leak
  // NOTHING else (the server sent no file at all).
  if (!loading && passwordRequired) {
    return (
      <div className="page-pad" style={{ maxWidth: 480, margin: '60px auto' }}>
        <div className="card" style={{ padding: 28 }}>
          <h2 style={{ marginTop: 0, textAlign: 'center' }}>
            {lockBandName ? `${lockBandName} — ${t('promoKit.title')}` : t('promoKit.title')}
          </h2>
          <p style={{ color: 'var(--text-dim)', textAlign: 'center', marginBottom: 18 }}>
            {t('sharePromo.passwordProtected')}
          </p>
          <form onSubmit={unlock}>
            <PasswordInput
              className="form-input"
              autoFocus
              value={pw}
              placeholder={t('sharePromo.passwordPh')}
              onChange={(e) => setPw(e.target.value)}
              containerStyle={{ marginBottom: 12 }}
            />
            {pwError && (
              <p style={{ color: 'var(--danger)', fontSize: '0.85rem', margin: '0 0 12px', textAlign: 'center' }} role="alert">
                {pwError}
              </p>
            )}
            <button className="btn btn-primary" type="submit" disabled={pwBusy || !pw} style={{ width: '100%' }}>
              {pwBusy ? t('sharePromo.unlocking') : t('sharePromo.unlock')}
            </button>
          </form>
          <p style={{ color: 'var(--text-dim)', textAlign: 'center', fontSize: '0.85rem', marginBottom: 0, marginTop: 16 }}>
            {t('sharePromo.noLogin')}
          </p>
        </div>
      </div>
    );
  }

  // Lot 3a — a genuine load failure keeps its own state (readable + retryable);
  // a rejected token falls through to the token message below (unchanged).
  if (loadError) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', paddingTop: 40 }}>
        <PageLoadError
          title={t('promoKit.title')}
          detail={loadError}
          onRetry={() => setRetryToken((n) => n + 1)}
        />
      </div>
    );
  }

  return (
    <div ref={denseRef} className="page" style={{ maxWidth: 720, margin: '0 auto', paddingTop: 40 }}>
      {loading && <p className="text-dim">{t('promoKit.loading')}</p>}
      {error && (
        <div className="card">
          <p style={{ color: 'var(--danger)' }}>{error}</p>
        </div>
      )}
      {!loading && !error && data && (
        <div className="card">
          {data.content ? (
            // Option C (2026-10-19): authored page — header/sections/bio/quotes/
            // contact. Same guarded download URLs, same band info.
            <PromoContentPage data={data} content={data.content} />
          ) : (
            <>
          {data.band.image_url && (
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <img
                src={data.band.image_url}
                alt={data.band.name}
                style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }}
              />
            </div>
          )}
          <h1 style={{ textAlign: 'center' }}>{data.band.name} — {t('promoKit.title')}</h1>
          <p className="text-dim" style={{ textAlign: 'center', marginTop: 0 }}>
            {t('promoKit.intro')}
          </p>

          {data.files.length === 0 && (
            <p className="text-dim" style={{ textAlign: 'center', marginTop: 24 }}>
              {t('promoKit.noFiles')}
            </p>
          )}

          {data.files.length > 0 && (
            <table className="table tbl-dense" style={{ width: '100%', marginTop: 16 }}>
              <thead>
                <tr>
                  <th>{t('files.colFile')}</th>
                  <th>{t('files.colSize')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.files.map((f) => (
                  <tr key={f.id}>
                    <td>{f.filename}</td>
                    <td className="text-dim">{fmtSize(f.size)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <a
                        className="btn btn-primary btn-small"
                        href={f.url}
                        target="_blank"
                        rel="noreferrer"
                        download={f.filename}
                      >
                        {t('promoKit.download')}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
